/* Modulo: Collection Rail — lead-tile scroll-snap controller.
   Native rail (works with no JS); JS wires arrows, page dots, autoplay with a
   progress bar + visible pause, a staggered scroll-in reveal, and a touch
   "centered card" class. Vanilla IIFE, reduced-motion aware, theme-editor safe. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  var REDUCE = !!(mq && mq.matches);
  if (mq && mq.addEventListener) mq.addEventListener('change', function (e) { REDUCE = e.matches; });

  function init(root) {
    if (!root || root.dataset.moduloCrReady === '1') return;
    root.dataset.moduloCrReady = '1';

    var rail = root.querySelector('[data-cr-rail]');
    if (!rail) return;
    var cards = Array.prototype.slice.call(rail.querySelectorAll('.modulo-crl__card'));
    if (cards.length < 2) return;

    var prev = root.querySelector('[data-cr-prev]');
    var next = root.querySelector('[data-cr-next]');
    var dotsEl = root.querySelector('[data-cr-dots]');
    var pp = root.querySelector('[data-cr-pp]');
    var prog = root.querySelector('[data-cr-prog]');
    var progWrap = root.querySelector('[data-cr-progwrap]');

    var wantsAuto = root.dataset.autoplay === '1';
    var interval = (parseFloat(root.dataset.interval, 10) || 6) * 1000;
    var autoOn = false, autoWanted = wantsAuto, autoTimer = null;

    /* ── geometry (use a standard, non-lead step) ── */
    function step() {
      if (cards.length > 2) return cards[2].offsetLeft - cards[1].offsetLeft;
      return cards[1].offsetLeft - cards[0].offsetLeft || cards[0].offsetWidth;
    }
    function perView() { var s = step(); return s ? Math.max(1, Math.round(rail.clientWidth / s)) : 1; }
    function pageCount() { return Math.max(1, Math.ceil(cards.length / perView())); }
    function maxScroll() { return rail.scrollWidth - rail.clientWidth; }
    function activePage() {
      var s = step(); if (!s) return 0;
      var pv = perView();
      return Math.min(pageCount() - 1, Math.max(0, Math.round(rail.scrollLeft / (s * pv))));
    }

    /* ── dots (page-based) ── */
    function buildDots() {
      if (!dotsEl) return;
      while (dotsEl.firstChild) dotsEl.removeChild(dotsEl.firstChild);
      var n = pageCount();
      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'modulo-crl__dot';
        b.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + n);
        (function (idx) { b.addEventListener('click', function () { stopAuto(); goPage(idx); }); })(i);
        dotsEl.appendChild(b);
      }
      syncDots();
    }
    function syncDots() {
      if (!dotsEl) return;
      var a = activePage();
      var ds = dotsEl.querySelectorAll('.modulo-crl__dot');
      for (var i = 0; i < ds.length; i++) {
        if (i === a) ds[i].setAttribute('aria-current', 'true');
        else ds[i].removeAttribute('aria-current');
      }
    }
    function syncArrows() {
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= maxScroll() - 2;
    }
    function sync() { syncDots(); syncArrows(); }

    function scrollToLeft(px) {
      px = Math.max(0, Math.min(maxScroll(), px));
      rail.scrollTo({ left: px, behavior: REDUCE ? 'auto' : 'smooth' });
    }
    function snapToNearest(px) { var s = step(); return s ? Math.round(px / s) * s : px; }
    function goPage(p) { var s = step(), pv = perView(); scrollToLeft(snapToNearest(p * s * pv)); }
    function page(dir) { var s = step(), pv = perView(); scrollToLeft(snapToNearest(rail.scrollLeft + dir * s * pv)); }

    if (prev) prev.addEventListener('click', function () { stopAuto(); page(-1); });
    if (next) next.addEventListener('click', function () { stopAuto(); page(1); });

    var raf;
    rail.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
    }, { passive: true });

    // keyboard: tab to a partially-off card scrolls it into view
    cards.forEach(function (c) {
      c.addEventListener('focus', function () { c.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: REDUCE ? 'auto' : 'smooth' }); });
    });

    /* ── staggered reveal (gated) ── */
    if (!REDUCE && 'IntersectionObserver' in window) {
      var revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); } });
      }, { root: null, threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
      cards.forEach(function (c) { revealIO.observe(c); });
    } else {
      root.classList.remove('modulo-crl--motion');
      cards.forEach(function (c) { c.classList.add('is-in'); });
    }

    /* ── centered card (touch parity) ── */
    if ('IntersectionObserver' in window) {
      var centerIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-centered', e.intersectionRatio >= 0.75); });
      }, { root: rail, threshold: [0.75] });
      cards.forEach(function (c) { centerIO.observe(c); });
    }

    /* ── autoplay + progress ── */
    function autoplayAllowed() { return autoWanted && !REDUCE && window.innerWidth > 860 && root.isConnected; }
    function resetProgress(run) {
      if (!prog || !progWrap) return;
      prog.style.transition = 'none';
      prog.style.transform = 'scaleX(0)';
      progWrap.classList.remove('is-anim');
      void prog.offsetWidth;
      if (run) {
        progWrap.classList.add('is-anim');
        prog.style.transitionDuration = (interval / 1000) + 's';
        prog.style.transform = 'scaleX(1)';
      }
    }
    function tick() {
      if (rail.scrollLeft >= maxScroll() - 2) scrollToLeft(0);
      else page(1);
    }
    function startAuto() {
      if (!autoplayAllowed()) return;
      autoOn = true;
      if (pp) {
        pp.classList.remove('is-paused');
        pp.setAttribute('aria-label', 'Pause autoplay');
        pp.setAttribute('aria-pressed', 'true');
      }
      clearInterval(autoTimer);
      resetProgress(true);
      autoTimer = setInterval(function () { tick(); resetProgress(true); }, interval);
    }
    function stopAuto() {
      autoOn = false;
      clearInterval(autoTimer); autoTimer = null;
      if (pp) {
        pp.classList.add('is-paused');
        pp.setAttribute('aria-label', 'Play autoplay');
        pp.setAttribute('aria-pressed', 'false');
      }
      resetProgress(false);
    }
    if (pp) {
      pp.addEventListener('click', function () {
        if (autoOn) { autoWanted = false; stopAuto(); }
        else { autoWanted = true; if (autoplayAllowed()) startAuto(); }
      });
    }
    if (window.matchMedia('(hover:hover)').matches) {
      rail.addEventListener('mouseenter', function () { if (autoOn) { clearInterval(autoTimer); resetProgress(false); } });
      rail.addEventListener('mouseleave', function () { if (autoOn) startAuto(); });
    }
    rail.addEventListener('touchstart', function () { if (autoOn) { autoWanted = false; stopAuto(); } }, { passive: true });

    /* ── resize / orientation ── */
    var rzT;
    function onResize() {
      clearTimeout(rzT);
      rzT = setTimeout(function () {
        var p = activePage();
        buildDots();
        var s = step(), pv = perView();
        rail.scrollTo({ left: snapToNearest(p * s * pv), behavior: 'auto' });
        sync();
        if (autoOn && !autoplayAllowed()) stopAuto();
      }, 150);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    root._crStop = function () {
      clearInterval(autoTimer); autoTimer = null;
      clearTimeout(rzT);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };

    buildDots();
    sync();
    if (autoplayAllowed()) startAuto();
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-crl]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-crl]') : null;
    if (el && typeof el._crStop === 'function') el._crStop();
  });
})();
